// Import 3rd-party dependencies
import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { SubSink } from 'subsink';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

// Import project-specific dependencies
import { AddFavoriteDialogComponent } from '@app/hub/add-favorite-dialog/add-favorite-dialog.component';
import { EditFavoriteDialogComponent } from '@app/hub/favorite-widget/edit-favorite-dialog/edit-favorite-dialog.component';
import { IFavoriteResourceLink, FavoriteService } from '@app/services/favorites/favorites.service';
import { FAVORITES_WIDGET_COMPONENT_CONSTS } from '@app/config/constants';

import {
  EFavoriteSnackBarAction,
  FavoriteSnackbarComponent,
  FavoriteSnackBarData
} from '@app/shared/components/snackbar/favorite-snackbar.component';

enum SortType {
  GROUP = 'Group',
  ALPHA = 'Alpha'
}

@Component({
  selector: 'app-favorite-widget',
  templateUrl: './favorite-widget.component.html',
  styleUrls: ['./favorite-widget.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FavoriteWidgetComponent implements OnInit, OnDestroy, AfterViewInit {
  private subs: SubSink;

  // Class attributes
  selected: string = SortType.ALPHA;
  snackBarAction: number;

  get sortType(): typeof SortType {
    return SortType;
  }

  consts = FAVORITES_WIDGET_COMPONENT_CONSTS;

  changeSortSubject$: BehaviorSubject<SortType> = new BehaviorSubject(SortType.ALPHA);

  favData$ = this.changeSortSubject$.pipe(
    switchMap((sort) => {
      switch (sort) {
        case SortType.ALPHA:
          return this.favoriteService.favorites$.pipe(
            map((favs) => favs.sort((f1, f2) => (f1.name < f2.name ? -1 : f1.name > f2.name ? 1 : 0))),
            map((favs) => ({ type: SortType.ALPHA, value: favs })),
            catchError(() => of([]))
          );
        case SortType.GROUP:
          const custom = this.favoriteService.favorites$.pipe(map((fav) => fav.filter((f) => f.customLink === true)));
          const regular = this.favoriteService.favorites$.pipe(map((fav) => fav.filter((f) => f.customLink === false)));
          return combineLatest([custom, regular]).pipe(
            map(([customArr, regArr]) => ({ type: SortType.GROUP, value: [customArr, regArr] }))
          );
        default:
          return this.favoriteService.favorites$;
      }
    })
  );

  isListEmpty$ = this.favData$.pipe(
    switchMap((favData) => {
      let ret = true;
      const { type, value } = favData as { type: SortType; value: unknown };
      if (type === SortType.ALPHA && (<[]>value).length > 0) {
        ret = false;
      } else if (type === SortType.GROUP) {
        const [custom = [], reg = []] = <[][]>value;
        if (custom.length > 0 || reg.length > 0) {
          ret = false;
        }
      }
      return of(ret);
    })
  );

  constructor(
    public addDialog: MatDialog,
    public editDialog: MatDialog,
    private favoriteService: FavoriteService,
    private _snackBar: MatSnackBar
  ) {}

  ngAfterViewInit(): void {
    this.changeSortSubject$.next(SortType.ALPHA);
  }

  ngOnInit(): void {
    this.subs = new SubSink();
    this.favoriteService.getFavoriteList();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  changeSort(event: { value: string }): void {
    this.changeSortSubject$.next(event.value as SortType);
  }

  unfavoriteLink(link: IFavoriteResourceLink): void {
    this.favoriteService.removeFavorite(link);
    this.openSnackBar(link, EFavoriteSnackBarAction.Unfavorite);
  }

  editFavoriteLink(link: IFavoriteResourceLink): void {
    this.editFavoritesDialog(link);
  }

  openSnackBar(linkInfo: IFavoriteResourceLink | IFavoriteResourceLink[], action: number, message?: string): void {
    const snackBarData: FavoriteSnackBarData = {
      linkInfo,
      action,
      message
    };

    const config: MatSnackBarConfig = {
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
      panelClass: 'favorite-snackbar',
      duration: 5000,
      data: snackBarData
    };
    this._snackBar.openFromComponent(FavoriteSnackbarComponent, config);
  }

  editFavoritesDialog(link: IFavoriteResourceLink): void {
    const dialogRef = this.editDialog.open(EditFavoriteDialogComponent, {
      data: link,
      panelClass: 'favorites-dialog-container'
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result: IFavoriteResourceLink) => {
      if (result) {
        this.favoriteService.updateFavorite(link, result);
        this.openSnackBar(result, EFavoriteSnackBarAction.Managefavorite, this.consts.LABEL_SNACKBAR_EDIT);
      }
    });
  }

  openFavoritesDialog(): void {
    const dialogRef = this.addDialog.open(AddFavoriteDialogComponent, {
      panelClass: 'favorites-dialog-container'
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result: IFavoriteResourceLink[]) => {
      if (result) {
        this.favoriteService.addFavorite(result);
        this.openSnackBar(result, EFavoriteSnackBarAction.FavoriteMany);
      }
    });
  }

  openLink(url: string): void {
    window.open(url, '_blank');
  }
}
